import {Box, capitalize, Stack, Typography} from '@mui/material';
import React from 'react';

import {StyledGrid} from 'presentation/components';
import {
	AppointmentInstructions,
	ConfirmationCard,
} from 'presentation/pages/private/booking/components';
import {ProtocolCard} from 'presentation/pages/private/booking/components/ProtocolCard';
import {SquareLabelTime} from 'presentation/pages/private/booking/components/SquareLabelTime';
import {BookingTypeEnum, TELEMEDICINE_INFO} from 'presentation/pages/private/booking/constants';

import {translations} from 'presentation/translations-new';
import {DocumentContainer} from '../DocumentContainer';
import {useAppointmentInfo} from './hooks/useAppointmentInfo';
import {AppointmentInfoProps} from './types';
import {FavoriteDoctorsLikeButton} from 'presentation/modules/booking/favoriteDoctors/components/FavoriteDoctorsLikeButton';

const {attendanceUnit, attendance, professionalAndDate} = translations['pt-br'].commons;

export function AppointmentInfo({appointmentDetails}: AppointmentInfoProps) {
	const {
		isMobile,
		clinic,
		provider,
		protocol,
		specialty,
		date,
		appointmentIdPublic,
		bookingType,
		day,
		monthName,
		formatted,
		historyIconName,
		isTelehealth,
		isSurgery,
		iconName,
		templateColumns1Fr,
	} = useAppointmentInfo({appointmentDetails});

	return (
		<StyledGrid.Root p={isMobile ? 2 : 4}>
			<StyledGrid.Item sm={12} md={12} lg={8}>
				<Stack gap={2}>
					<ConfirmationCard.Root sx={{position: 'relative'}}>
						<ConfirmationCard.Header subheader={professionalAndDate}>
							<Stack flexDirection={'row'} gap={2} alignItems={'start'}>
								<SquareLabelTime day={day} month={monthName} />
								<Stack width={'100%'}>
									<Typography variant="subtitle2" fontSize={'1.125rem'}>
										{`${capitalize(formatted)}`}
									</Typography>
									{provider.name ? (
										<ConfirmationCard.Content
											iconName="medical-services-filled"
											primaryText={provider.name}
											height={'28px'}
										/>
									) : null}
									{specialty?.description ? (
										<ConfirmationCard.Content
											iconName={historyIconName}
											primaryText={specialty?.description}
										/>
									) : null}
								</Stack>
							</Stack>
							{specialty?.code && provider?.code ? (
								<FavoriteDoctorsLikeButton
									cdEspecialidade={Number(specialty.code)}
									cdSubEspecialidade={Number(
										appointmentDetails?.subSpecialtyCode,
									)}
									cdPrestador={Number(provider.code)}
									selected={appointmentDetails?.provider?.isFavorite}
									sx={{position: 'absolute', top: 16, right: 16}}
								/>
							) : null}
						</ConfirmationCard.Header>
					</ConfirmationCard.Root>
					<Box
						display="grid"
						gridTemplateColumns={templateColumns1Fr ? '1fr' : '1fr 1fr'}
						gap={2}>
						<ConfirmationCard.Root>
							<ConfirmationCard.Header
								subheader={isTelehealth ? attendance : attendanceUnit}
							/>
							<Stack>
								<ConfirmationCard.Content
									iconName={iconName}
									primaryText={
										isTelehealth ? TELEMEDICINE_INFO.name : clinic.name
									}
								/>
							</Stack>
						</ConfirmationCard.Root>
						{isSurgery ? null : <ProtocolCard protocolNumber={protocol} />}
					</Box>

					{isTelehealth ? <DocumentContainer documentId={appointmentIdPublic} /> : null}
				</Stack>
			</StyledGrid.Item>

			<StyledGrid.Item sm={12} md={12} lg={4}>
				<AppointmentInstructions
					appointment={{
						bookingType: bookingType as unknown as BookingTypeEnum,
						date,
						specialtyCode: specialty?.code,
						specialtyName: specialty?.description,
					}}
					isHistoryDetails
				/>
			</StyledGrid.Item>
		</StyledGrid.Root>
	);
}
